import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { EASE } from "./Reveal";

const LINES = [
  { text: "Maison", cls: "" },
  { text: "Pierre", cls: "italic text-[#d8b4fe] pl-[8vw] lg:pl-[6vw]" },
  { text: "& Co.", cls: "text-outline" },
];

const BUBBLES = [
  { src: "/img/sneakers.jpg", cls: "-left-6 top-[14%] h-20 w-20 md:h-24 md:w-24", d: "0s" },
  { src: "/img/perfume.jpg", cls: "-right-5 top-[38%] h-16 w-16 md:h-20 md:w-20", d: "1.4s" },
  { src: "/img/vest.jpg", cls: "left-[4%] bottom-[20%] h-16 w-16 md:h-[5.5rem] md:w-[5.5rem]", d: "2.6s" },
];

const Title = () => (
  <h1 className="font-display text-[19vw] font-medium leading-[0.84] tracking-[-0.03em] text-[#f3eefc] sm:text-[16vw] lg:text-[9.6vw]" data-testid="hero-title">
    {LINES.map((l, i) => (
      <span key={l.text} className="block overflow-hidden pb-[0.06em]">
        <motion.span
          className={`block ${l.cls}`}
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 1.3, delay: 0.25 + i * 0.14, ease: EASE }}
        >
          {l.text}
        </motion.span>
      </span>
    ))}
  </h1>
);

const Art = ({ mx, my, imgY }) => {
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 110, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), { stiffness: 110, damping: 18 });
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.6, delay: 0.5, ease: EASE }}
      style={{ perspective: 1200 }}
      className="relative mx-auto w-[78%] max-w-[440px] sm:w-[60%] lg:w-full"
    >
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <div className="relative aspect-[4/5] overflow-hidden rounded-t-[999px] border border-[#3b1d63] shadow-[0_40px_120px_-30px_rgba(124,58,237,0.6)]">
          <motion.img src="/img/brand.jpg" alt="Maison Pierre & Co. luxury finds" style={{ y: imgY }} className="absolute inset-0 h-[118%] w-full object-cover" data-testid="hero-brand-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#07030c] via-transparent to-transparent" />
          <p className="font-mono-j absolute bottom-5 left-0 right-0 text-center text-[11px] uppercase tracking-[0.35em] text-[#e9ddfb]">Dope Hits, Causing Fits</p>
        </div>
        {BUBBLES.map((b) => (
          <div key={b.src} className={`float absolute ${b.cls}`} style={{ animationDelay: b.d, transform: "translateZ(60px)" }}>
            <div className="h-full w-full overflow-hidden rounded-full border border-white/20 bg-white/5 p-1 backdrop-blur-md shadow-[0_0_40px_rgba(168,85,247,0.45)]">
              <img src={b.src} alt="" className="h-full w-full rounded-full object-cover" />
            </div>
          </div>
        ))}
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "6%"]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
    e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
  };

  return (
    <section ref={ref} onMouseMove={onMove} className="grain relative min-h-[100svh] overflow-hidden pt-28 pb-20 md:pt-32" data-testid="hero-section">
      <div className="spotlight pointer-events-none absolute inset-0" />
      <div className="pointer-events-none absolute -left-40 top-1/3 h-[520px] w-[520px] rounded-full bg-[#7c3aed]/25 blur-[140px]" />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-16 px-5 md:px-10 lg:grid-cols-12">
        <motion.div style={{ y: titleY }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 1 }} className="font-mono-j mb-8 text-xs uppercase tracking-[0.3em] text-[#c4a5f5]">
            Luxury &amp; designer finds — BC, Canada
          </motion.p>
          <Title />
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1, ease: EASE }} className="mt-10 flex max-w-xl flex-col gap-8">
            <p className="text-base leading-relaxed text-[#cbbbe6] md:text-lg">
              Curated luxury styles. Hoodies, shoes, colognes &amp; more. Claim now for personal service and Canada-wide shipping.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link to="/shop" data-testid="hero-shop-button" className="group inline-flex items-center gap-3 rounded-full bg-[#7c3aed] px-7 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#a855f7]">
                Shop the drop <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link to="/#request" data-testid="hero-request-button" className="rounded-full border border-[#3b1d63] px-7 py-4 text-sm text-[#e9ddfb] transition-colors duration-300 hover:border-[#d8b4fe]">
                Request an item
              </Link>
            </div>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5">
          <Art mx={mx} my={my} imgY={imgY} />
        </div>
      </div>
    </section>
  );
};
